import { Typography } from "@material-ui/core";
import { AttributesMixin, define, Events, Linked, memberOf, Store, type } from "@type-r/models";
import { useIO, useLinked, useModel } from "@type-r/react";
import { AddFilter, asCurrency, asDate, asTimelessDate, FiltersList, Flex, Loading, Page, PopupIODialog, SelectSortBy, SmallTile, TileField, TilesList, toLinkedBoolean, useLinkedQueryParam } from "lqx-react/lib";
import React from "react";
import { Menu, MenuItem } from "lqx-react";
import { CompanyModel, globalStore, PlatformCompany } from "api";
import { archiveRemittanceAdvice, RemittanceAdvice } from "../../api";
import {  UnresolvedRAFilters, UnresolvedRemittanceAdvice } from "../../api/unresolvedRemittanceAdvice";
import { RemittanceAdviceDetails } from "./details"
import { matchString } from "api/common";
import { Can } from "src/app/services/can";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";

interface State extends AttributesMixin<typeof State>{}

const sortRemittanceOptions = {
    executionDate: 'Payment Execution Date',
    estimatedPaymentDate: 'Estimated Payment Date',
    createdOn: 'Created Date',
};

@define class State extends Store {
    static attributes = {
        remittanceAdvices : [ UnresolvedRemittanceAdvice ],
        companies : [ PlatformCompany ],
        selected : memberOf('remittanceAdvices', UnresolvedRemittanceAdvice),
        remittanceAdviceToBeArchived: type(UnresolvedRemittanceAdvice).null,
        filter: type(UnresolvedRAFilters),
        sortBy : {
            attr: 'executionDate',
            asc : false
        }
    }
    
    async fetch(category){
        this.companies = globalStore.platformCompanies;
        
        await Promise.all([
            this.remittanceAdvices.fetch({ params : { category }}),
        ]);

        if (this.remittanceAdvices) {
            this.filter.initializeOptions(this.remittanceAdvices)
        }

        return this;
    }
}

export const UnresolvedRemittanceAdvices = ({$category, $loading}) => {
    const state = useModel( State );
    
    const $createdOnQueryParam = useLinkedQueryParam("createdOn");
    const { value: category } = $category;

    const isArchived = category === 'archived';

    const isReady = useIO( async () => {
        $loading.true();
        await state.fetch(category);
        $loading.false();
    }, [category]);

    const sortedAndFiltered = React.useMemo( () => {
        const { attr, asc } = state.sortBy;
        return state.remittanceAdvices
            .filter(ra => ra.filter(state.filter) )
            .filter(ra =>!$createdOnQueryParam || !$createdOnQueryParam.value || matchString(asTimelessDate(ra.createdOn), $createdOnQueryParam.value.toString() ))
            .sort( UnresolvedRemittanceAdvice.comparator( attr as any, asc ) );
    },
    [ state.remittanceAdvices['_changeToken'], state.sortBy['_changeToken'], state.filter["_changeToken"], $createdOnQueryParam.value ]);

    const $remittanceId = useLinked(null);
    const areDetailsReady = useIO(async () => {
        if (state.selected) {
          $remittanceId.set(state.selected.id);
          await state.selected.fetch();
        }
      }, [state.selected]);

      const archive = (remittanceAdvice: UnresolvedRemittanceAdvice) => {
        state.$.remittanceAdviceToBeArchived.set(remittanceAdvice);
      };

      const remittanceAdviceArchived = (remittanceAdviceId) => {
          state.$.selected.set(null);
          state.remittanceAdvices.remove(remittanceAdviceId);
      }

    return (
        isReady ?
        <Page
            menuWidth={530}
            maxWidth="xl"
            left={
                <>
                <FiltersList filter={state.filter} />
                <AddFilter
                    filter={state.filter}
                    filterAllPlaceholder="Filter by email address (from), subject..."
                />

                <Flex row>
                    <SelectSortBy $attr={ state.sortBy.$.attr } $asc={ state.sortBy.$.asc } options={ sortRemittanceOptions } />
                </Flex>

                {sortedAndFiltered.length ? (
                    <TilesList
                        items={sortedAndFiltered}
                        itemSize={64}
                        renderer={RemittanceTile(state.$.selected, archive, isArchived)}
                        selectedItem={state.selected}
                    />
                ) : (
                    <Flex center stretch>
                        <Typography variant="h4">No results found.</Typography>
                    </Flex>
                )}

                </>
            }
        >

            {!areDetailsReady ? (
                <Loading />
            ) : state.selected ? (
                <RemittanceAdviceDetails key={state.selected.id} remittanceAdvice={state.selected} companies={ state.companies } isArchived={isArchived} />
            ) : (
                <Flex center stretch>
                Select a remittance advice
                </Flex>
            )}

        {state.remittanceAdviceToBeArchived ? (
            <ArchiveDialog
                $remittanceAdvice={ state.$.remittanceAdviceToBeArchived }
                remittanceAdviceArchived={remittanceAdviceArchived}
            />
            ) : (
            undefined
        )}

        </Page> :
        <Loading />
    
    )
}

const RemittanceTile = ($isEditing, archive, isArchived) => ({ data, style, index }) => {
    const remittanceAdvice : UnresolvedRemittanceAdvice = data.items[ index ];

    return (
        <div style={style}>
            <SmallTile
                style={ style }
                color={ 'green' }
                isSelected={ $isEditing.value === remittanceAdvice }
                onClick={ () => $isEditing.set( remittanceAdvice.id ) }
                action={
                    !isArchived && Can(UserEntitlementCatalogs.BankEntryWrite) ?
                    <Menu>
                        <MenuItem onSelect={() => archive(remittanceAdvice)}>
                            Archive
                        </MenuItem>
                    </Menu> : undefined
                }
                footer={
                    <>
                    <TileField label="Remittance Amount">
                        { asCurrency( remittanceAdvice.totalInvoiceAmount, remittanceAdvice.currency ) }
                    </TileField>

                    <TileField label="Payment Execution Date">
                        { asDate( remittanceAdvice.executionDate ) }
                    </TileField>
                    </>
                }
            >
                { asDate(remittanceAdvice.createdOn) }
            </SmallTile>
        </div>
    )
}

export const ArchiveDialog = ({ $remittanceAdvice, remittanceAdviceArchived } : {
    $remittanceAdvice : Linked<RemittanceAdvice>,
    remittanceAdviceArchived : (remittanceAdviceId: number) => void
}) => {

    const $isOpen = toLinkedBoolean($remittanceAdvice);
    const remittanceAdvice = $remittanceAdvice.value;
    
    return (
        <PopupIODialog
            title={ "Archive Remittance Advice" }
            actionLabel="Archive"
            $isOpen={ $isOpen }
            success="Remittance archived"
            doIO={ async () => {
                const remittanceAdviceId = remittanceAdvice.id as any as number;

                const updated = await archiveRemittanceAdvice(remittanceAdviceId);

                if (updated) {
                    remittanceAdviceArchived(remittanceAdviceId);
                    Events.trigger('raSummary:refresh');
                }
            }} >
        </PopupIODialog>
    );
}
